import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {useLexicalNodeSelection} from '@lexical/react/useLexicalNodeSelection';
import {mergeRegister} from '@lexical/utils';
import {isDropboxEmoji} from 'components/DSYS/editor/plugins/EmojiPicker';
import {useRichTextMediaFeatures} from 'components/DSYS/editor/plugins/MediaFeaturesContext';
import type {BaseSelection, LexicalCommand, LexicalEditor, NodeKey} from 'lexical';
import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  $isRangeSelection,
  $setSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  createCommand,
  DRAGSTART_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import {Suspense, useCallback, useEffect, useRef, useState} from 'react';

import {ImageLayoutControls} from './ImageLayoutControls';
import {$isImageNode, ImageLayout} from './ImageNode';
import styles from './ImageNode.module.css';

const imageCache = new Set();

export const RIGHT_CLICK_IMAGE_COMMAND: LexicalCommand<MouseEvent> = createCommand(
  'RIGHT_CLICK_IMAGE_COMMAND'
);

function useSuspenseImage(src: string) {
  if (!imageCache.has(src)) {
    throw new Promise((resolve) => {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        imageCache.add(src);
        resolve(null);
      };
    });
  }
}

const LazyImage = ({
  draggable,
  altText,
  className,
  style,
  imageRef,
  src,
  width,
  height,
}: {
  draggable: boolean;
  altText: string;
  className?: string;
  height: 'inherit' | 'auto' | number;
  imageRef: {current: null | HTMLImageElement};
  style: React.CSSProperties;
  src: string;
  width: 'inherit' | '100%' | number;
}) => {
  useSuspenseImage(src);
  return (
    <img
      draggable={draggable}
      className={className || undefined}
      src={src}
      alt={altText}
      ref={imageRef}
      style={{
        ...style,
        maxWidth: '100%',
        height,
        width,
      }}
    />
  );
};

export const ImageComponent = ({
  src,
  altText,
  nodeKey,
  width,
  height,
  layout = 'inline',
}: {
  altText: string;
  height: 'inherit' | number;
  layout?: ImageLayout;
  nodeKey: NodeKey;
  src: string;
  width: 'inherit' | number;
}) => {
  const imageRef = useRef<null | HTMLImageElement>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const [editor] = useLexicalComposerContext();
  const [selection, setSelection] = useState<BaseSelection | null>(null);
  const activeEditorRef = useRef<LexicalEditor | null>(null);
  const isEditable = editor.isEditable();

  const isEmoji = isDropboxEmoji(src);
  const {imageLayout: imageLayoutEnabled} = useRichTextMediaFeatures();
  const isWrapped = layout !== 'inline';

  const onDelete = useCallback(
    (payload: KeyboardEvent) => {
      if (isSelected && $isNodeSelection($getSelection())) {
        const event: KeyboardEvent = payload;
        event.preventDefault();
        const node = $getNodeByKey(nodeKey);
        if ($isImageNode(node)) {
          node.remove();
          return true;
        }
      }
      return false;
    },
    [isSelected, nodeKey]
  );

  const onEnter = useCallback(
    (event: KeyboardEvent) => {
      const latestSelection = $getSelection();
      const buttonElem = buttonRef.current;
      if (
        isSelected &&
        $isNodeSelection(latestSelection) &&
        latestSelection.getNodes().length === 1
      ) {
        if (buttonElem !== null && buttonElem !== document.activeElement) {
          event.preventDefault();
          buttonElem.focus();
          return true;
        }
      }
      return false;
    },
    [isSelected]
  );

  const onEscape = useCallback(
    (event: KeyboardEvent) => {
      if (buttonRef.current === event.target) {
        $setSelection(null);
        editor.update(() => {
          setSelected(true);
          const parentRootElement = editor.getRootElement();
          if (parentRootElement !== null) {
            parentRootElement.focus();
          }
        });
        return true;
      }
      return false;
    },
    [editor, setSelected]
  );

  const onClick = useCallback(
    (payload: MouseEvent) => {
      if (!isEditable) {
        return false;
      }

      // Don't allow clicking/selecting emoji images
      if (isEmoji) {
        return false;
      }

      const event = payload;

      if (event.target === imageRef.current) {
        if (event.shiftKey) {
          setSelected(!isSelected);
        } else {
          clearSelection();
          setSelected(true);
        }
        return true;
      }

      return false;
    },
    [isEditable, isEmoji, isSelected, setSelected, clearSelection]
  );

  const onRightClick = useCallback(
    (event: MouseEvent): void => {
      editor.getEditorState().read(() => {
        const latestSelection = $getSelection();
        const domElement = event.target as HTMLElement;
        if (
          domElement.tagName === 'IMG' &&
          $isRangeSelection(latestSelection) &&
          latestSelection.getNodes().length === 1
        ) {
          editor.dispatchCommand(RIGHT_CLICK_IMAGE_COMMAND, event as MouseEvent);
        }
      });
    },
    [editor]
  );

  useEffect(() => {
    let isMounted = true;
    const rootElement = editor.getRootElement();
    const unregister = mergeRegister(
      editor.registerUpdateListener(({editorState}) => {
        if (isMounted) {
          setSelection(editorState.read(() => $getSelection()));
        }
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_, activeEditor) => {
          activeEditorRef.current = activeEditor;
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand<MouseEvent>(CLICK_COMMAND, onClick, COMMAND_PRIORITY_LOW),
      editor.registerCommand<MouseEvent>(RIGHT_CLICK_IMAGE_COMMAND, onClick, COMMAND_PRIORITY_LOW),
      editor.registerCommand(
        DRAGSTART_COMMAND,
        (event) => {
          if (event.target === imageRef.current) {
            // TODO This is just a temporary workaround for FF to behave like other browsers.
            // Ideally, this handles drag & drop too (and all browsers).
            event.preventDefault();
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(KEY_DELETE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_BACKSPACE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_ENTER_COMMAND, onEnter, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_ESCAPE_COMMAND, onEscape, COMMAND_PRIORITY_LOW)
    );

    rootElement?.addEventListener('contextmenu', onRightClick);

    return () => {
      isMounted = false;
      unregister();
      rootElement?.removeEventListener('contextmenu', onRightClick);
    };
  }, [
    clearSelection,
    editor,
    isSelected,
    nodeKey,
    onDelete,
    onEnter,
    onEscape,
    onClick,
    onRightClick,
    setSelected,
  ]);

  // Only show focus styling when the image is directly selected (NodeSelection),
  // not when it's part of a table row/column selection
  const isDirectNodeSelection = $isNodeSelection(selection);
  const draggable = isEditable && isSelected && isDirectNodeSelection && !isEmoji;
  const isFocused = isEditable && isSelected && isDirectNodeSelection && !isEmoji;
  const showLayoutFrame = imageLayoutEnabled && !isEmoji;

  const image = (
    <LazyImage
      draggable={draggable}
      style={{verticalAlign: 'middle', marginBottom: isEmoji ? 4 : isFocused ? 1 : 6}}
      className={
        isFocused
          ? `${styles.focused} ${$isNodeSelection(selection) ? styles.draggable : ''}`
          : undefined
      }
      src={src}
      altText={altText}
      imageRef={imageRef}
      // Wrapped images size to their 45%-wide column instead of their stored dimensions.
      width={isWrapped ? '100%' : width}
      height={isWrapped ? 'auto' : height}
    />
  );

  return (
    <Suspense fallback={null}>
      {showLayoutFrame ? (
        <span className={styles.layoutFrame}>
          {image}
          {isFocused && <ImageLayoutControls layout={layout} nodeKey={nodeKey} />}
        </span>
      ) : (
        image
      )}
    </Suspense>
  );
};
